import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SEO } from "@/components/seo/SEO";
import { 
  Home, 
  Zap, 
  BarChart3, 
  Users, 
  Leaf, 
  TrendingUp, 
  Shield, 
  Network,
  Settings,
  Database,
  UserCheck,
  Euro,
  CheckCircle,
  ArrowRight,
  Phone,
  Mail
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export default function SmartHomeEnergyHub() {
  const [formData, setFormData] = useState({
    naam: "",
    email: "",
    telefoon: "",
    adres: "",
    opmerkingen: ""
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { error } = await supabase
        .from('energy_hub_registrations')
        .insert([{
          naam: formData.naam,
          email: formData.email,
          telefoon: formData.telefoon,
          adres: formData.adres,
          opmerkingen: formData.opmerkingen
        }]);

      if (error) throw error;

      toast.success("Aanmelding succesvol verzonden! We nemen binnenkort contact met u op.");
      setFormData({ naam: "", email: "", telefoon: "", adres: "", opmerkingen: "" });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error("Er is een fout opgetreden. Probeer het opnieuw of neem direct contact op.");
    }
  };

  const benefits = [
    {
      icon: <Euro className="h-8 w-8 text-primary" />,
      title: "Lagere Energierekening",
      description: "Door meer zelfopgewekte energie te verbruiken, besparen leden op de energierekening."
    },
    {
      icon: <Leaf className="h-8 w-8 text-primary" />,
      title: "Actieve bijdrage aan het milieu",
      description: "U draagt direct bij aan de energietransitie en vermindert de afhankelijkheid van fossiele brandstoffen."
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-primary" />,
      title: "Waardeverhoging van uw woning",
      description: "Slimme technologie en een duurzaam energienetwerk maken uw woning toekomstbestendig."
    },
    {
      icon: <Users className="h-8 w-8 text-primary" />,
      title: "Sterkere Lokale Gemeenschap",
      description: "De coöperatie verbindt buren en bedrijven in de regio om samen te werken aan een groter doel."
    },
    {
      icon: <Shield className="h-8 w-8 text-primary" />,
      title: "Onzekerheden verminderen",
      description: "De netbeheerders ondersteunen initiatieven zoals deze, omdat ze de schaarse ruimte op het net optimaal benutten."
    }
  ];

  const workingSteps = [
    {
      icon: <Settings className="h-12 w-12 text-primary" />,
      title: "Slimme Installatie",
      description: "Wij werken samen met installateurs om smart home-apparaten (zoals slimme boilers en laadpalen) en een energiemanagementsysteem te installeren om energieverbruik te optimaliseren."
    },
    {
      icon: <Database className="h-12 w-12 text-primary" />,
      title: "Data-analyse & Advies",
      description: "Op basis van uw verbruiksdata krijgt u gepersonaliseerd advies over het efficiëntste energiegebruik."
    },
    {
      icon: <Network className="h-12 w-12 text-primary" />,
      title: "Coöperatieve Synergie",
      description: "Samen creëren we een collectieve buffer. Apparaten worden automatisch aangestuurd op momenten dat er veel lokaal opgewekte zonne-energie beschikbaar is. Dit vermindert de piekbelasting en zorgt voor een stabieler net."
    }
  ];

  const faqData = [
    {
      question: "Wat is een energy hub?",
      answer: "Een groep bedrijven of, in ons geval, woningen die samenwerken om lokaal energie te beheren, op te slaan en te gebruiken. Door samen te werken kunnen we het elektriciteitsnet ontlasten en efficiënter omgaan met lokaal opgewekte zonne-energie."
    },
    {
      question: "Wat is een groeps-CBC?",
      answer: "Dit is een contract met de netbeheerder waarbij de groep afspreekt minder stroom te verbruiken tijdens piekmomenten. Door als coöperatie samen te werken, kunnen we flexibiliteit bieden aan het net en daar ook financieel van profiteren."
    },
    {
      question: "Wat is een GTO?",
      answer: "GTO staat voor Gezamenlijke Transport Overeenkomst. Dit is een toekomstige contractvorm die de gezamenlijke transportcapaciteit vastlegt. Hierdoor kunnen we als groep beter plannen en het net efficiënter gebruiken."
    },
    {
      question: "Moet ik mijn eigen installateur meenemen?",
      answer: "Nee, dat hoeft niet. Wij hebben een netwerk van gecertificeerde installateurs die bekend zijn met de nieuwste technologie en de specifieke behoeften in onze regio. U kunt natuurlijk ook uw eigen installateur inbrengen, mits deze voldoet aan onze kwaliteitseisen."
    },
    {
      question: "Hoe zit het met de kosten?",
      answer: "De initiële investering in slimme apparatuur wordt gedekt door subsidies, leningen en de coöperatie. Door de besparingen op uw energierekening en eventuele inkomsten uit netdiensten verdient de investering zich binnen enkele jaren terug. We maken voor elk lid een maatwerk financieel plan."
    },
    {
      question: "Hoe lang duurt een installatie?",
      answer: "Een standaard installatie duurt meestal 1-2 dagen, afhankelijk van de complexiteit van uw woning en de gekozen apparatuur. Onze installateurs zorgen voor minimale overlast en een nette afwerking."
    },
    {
      question: "Wat gebeurt er als ik verhuiz?",
      answer: "De slimme installaties verhogen de waarde van uw woning. Bij verkoop kunt u dit als extra troef gebruiken. Het nieuwe lidmaatschap kan vaak worden overgedragen naar de nieuwe bewoner, of u kunt uw lidmaatschap meenemen naar uw nieuwe woning."
    }
  ];

  return (
    <>
      <SEO 
        title="Smart Home Energy Hub Coöperatie | Sol-Vi Pannerden"
        description="Sluit je aan bij onze Smart Home Energy Hub coöperatie in Pannerden. Samen maken we woningen slim en ontlasten we het elektriciteitsnet. Lagere energierekening gegarandeerd."
        canonical="https://sol-vi.nl/smart-home-energy-hub"
        keywords="smart home, energy hub, coöperatie, Pannerden, zonnepanelen, energiebesparing, slimme meter, elektriciteitsnet"
      />

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-primary/10 via-background to-accent/5 py-20 lg:py-32">
          <div className="absolute inset-0 bg-grid-pattern opacity-5" />
          <div className="container relative mx-auto px-4">
            <div className="mx-auto max-w-4xl text-center">
              <h1 className="mb-6 text-4xl font-bold leading-tight md:text-6xl lg:text-7xl text-foreground">
                Samen voor een <span className="text-primary">Slimme Toekomst</span> in Pannerden
              </h1>
              <p className="mb-8 text-xl text-muted-foreground lg:text-2xl">
                Wij verbinden bewoners, installateurs en data om lokaal energie te beheren en het elektriciteitsnet te versterken.
              </p>
              <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
                <Button size="lg" className="text-lg" onClick={() => document.getElementById('aanmelden')?.scrollIntoView({ behavior: 'smooth' })}>
                  Doe mee en word lid
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button variant="outline" size="lg" className="text-lg" onClick={() => document.getElementById('hoe-het-werkt')?.scrollIntoView({ behavior: 'smooth' })}>
                  Ontdek hoe het werkt
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-4xl">
              <h2 className="mb-8 text-center text-3xl font-bold md:text-4xl text-foreground">
                Onze Missie: Samen bouwen aan een Slimme Energiehub
              </h2>
              <div className="grid gap-8 md:grid-cols-3">
                <Card className="border-l-4 border-l-red-500">
                  <CardHeader>
                    <CardTitle className="text-red-600">Het Probleem</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Het elektriciteitsnet in de regio Flevoland, Gelderland en Utrecht (FGU) is overbelast. 
                      Dit maakt het moeilijk voor nieuwe grootverbruikers om zich aan te sluiten.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-yellow-500">
                  <CardHeader>
                    <CardTitle className="text-yellow-600">Onze Oplossing</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Onze coöperatie bouwt een energy hub op lokaal niveau. We focussen op woningen 
                      en verhogen het directe verbruik van lokaal opgewekte stroom.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-green-500">
                  <CardHeader>
                    <CardTitle className="text-green-600">Unieke Waarde</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      In tegenstelling tot traditionele aanpak, ontlasten we het net door slimme sturing 
                      van huishoudelijke apparaten op momenten van piekproductie.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* How it Works Section */}
        <section id="hoe-het-werkt" className="py-20">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-6xl">
              <h2 className="mb-4 text-center text-3xl font-bold md:text-4xl text-foreground">
                Hoe werkt onze Smart Home Energy Hub?
              </h2>
              <p className="mb-12 text-center text-xl text-muted-foreground">
                Wij werken samen met lokale installateurs en gebruiken keiharde data om uw woning 
                te transformeren in een slimme energiecel.
              </p>
              
              <div className="grid gap-8 md:grid-cols-3">
                {workingSteps.map((step, index) => (
                  <Card key={index} className="text-center">
                    <CardHeader>
                      <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
                        {step.icon}
                      </div>
                      <CardTitle className="text-xl">{step.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{step.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-6xl">
              <h2 className="mb-12 text-center text-3xl font-bold md:text-4xl text-foreground">
                Waarom meedoen?
              </h2>
              
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {benefits.map((benefit, index) => (
                  <Card key={index} className="h-full">
                    <CardHeader>
                      <div className="mb-2">{benefit.icon}</div>
                      <CardTitle className="text-xl">{benefit.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{benefit.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Partners Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-4xl text-center">
              <h2 className="mb-8 text-3xl font-bold md:text-4xl text-foreground">
                Samen Sterk
              </h2>
              <p className="mb-12 text-xl text-muted-foreground">
                Wij werken samen met lokale installateurs die bekend zijn met de nieuwste technologie 
                en de specifieke behoeften in onze regio.
              </p>
              
              <div className="grid gap-8 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <UserCheck className="mx-auto h-12 w-12 text-primary" />
                    <CardTitle>Gecertificeerde Installateurs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Ons netwerk bestaat uit lokale installateurs met bewezen expertise in 
                      slimme energiesystemen en smart home technologie.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <Network className="mx-auto h-12 w-12 text-primary" />
                    <CardTitle>Netbeheerder Liander</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Liander ondersteunt ons initiatief als partner in de energietransitie en 
                      helpt bij het optimaliseren van de netcapaciteit.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-4xl">
              <h2 className="mb-12 text-center text-3xl font-bold md:text-4xl text-foreground">
                Vragen en antwoorden
              </h2>
              
              <Accordion type="single" collapsible className="w-full">
                {faqData.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left text-lg font-semibold">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* Contact & Registration Section */}
        <section id="aanmelden" className="py-20">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-4xl">
              <div className="text-center mb-12">
                <h2 className="mb-4 text-3xl font-bold md:text-4xl text-foreground">
                  Klaar om mee te doen?
                </h2>
                <p className="text-xl text-muted-foreground">
                  Sluit je aan bij onze coöperatie en word onderdeel van de oplossing voor de energietoekomst van onze regio.
                </p>
              </div>

              <div className="grid gap-8 lg:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Meld je aan!</CardTitle>
                    <CardDescription>
                      Vul onderstaand formulier in en we nemen binnenkort contact met u op voor een vrijblijvend gesprek.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <Label htmlFor="naam">Naam *</Label>
                        <Input
                          id="naam"
                          value={formData.naam}
                          onChange={(e) => setFormData({...formData, naam: e.target.value})}
                          required
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="email">E-mailadres *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          required
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="telefoon">Telefoonnummer *</Label>
                        <Input
                          id="telefoon"
                          type="tel"
                          value={formData.telefoon}
                          onChange={(e) => setFormData({...formData, telefoon: e.target.value})}
                          required
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="adres">Adres *</Label>
                        <Input
                          id="adres"
                          value={formData.adres}
                          onChange={(e) => setFormData({...formData, adres: e.target.value})}
                          placeholder="Straat, huisnummer, postcode, plaats"
                          required
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="opmerkingen">Opmerkingen</Label>
                        <Textarea
                          id="opmerkingen"
                          value={formData.opmerkingen}
                          onChange={(e) => setFormData({...formData, opmerkingen: e.target.value})}
                          placeholder="Eventuele vragen of specifieke wensen..."
                          rows={3}
                        />
                      </div>
                      
                      <Button type="submit" className="w-full" size="lg">
                        Meld je aan!
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Direct contact</CardTitle>
                    <CardDescription>
                      Heeft u vragen? Neem gerust direct contact met ons op.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center space-x-3">
                      <Phone className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-semibold">Telefoon</p>
                        <p className="text-muted-foreground">026 442 3580</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <Mail className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-semibold">E-mail</p>
                        <p className="text-muted-foreground">info@sol-vi.nl</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <Home className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-semibold">Adres</p>
                        <p className="text-muted-foreground">
                          Pannerdensche Kerkweg 5<br />
                          6909 BL Pannerden
                        </p>
                      </div>
                    </div>

                    <div className="pt-4 border-t">
                      <h3 className="font-semibold mb-2">Wat kunt u verwachten?</h3>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li className="flex items-center">
                          <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                          Binnen 48 uur contact voor kennismaking
                        </li>
                        <li className="flex items-center">
                          <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                          Gratis woningcheck en energieadvies
                        </li>
                        <li className="flex items-center">
                          <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                          Maatwerk voorstel voor uw situatie
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}